import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
// import { useVisitorData } from '../../contexts/VisitorContext';
import api from '../../services/api';
import { visitorAPI } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { toast } from 'react-hot-toast';
// import * as utils from '../../utils/dateUtils';
import PageTitle from '../shared/PageTitle';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

const Visitors = () => {
  const { currentUser } = useContext(AuthContext);
  const { settings } = useSettings();
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  const [visitors, setVisitors] = useState([]);
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Visitor form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [formData, setFormData] = useState({
    visitor_name: '',
    villa_id: '',
    purpose: '',
    phone: '',
    expected_date: formatDate(new Date(Date.now() + 24 * 60 * 60 * 1000)), // Default to tomorrow
    expected_time: '14:00',
    expected_duration: '1',
    notes: ''
  });

  // Filters
  // const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Selected visitor for details view
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Add state variables for direct image sources
  const [directVisitorImageSrc, setDirectVisitorImageSrc] = useState('');
  const [directIdDocImageSrc, setDirectIdDocImageSrc] = useState('');

  // Add refs for image elements
  // const visitorImageRef = useRef(null);
  // const idDocumentImageRef = useRef(null);

  // Add a new state for active tab
  const [activeTab, setActiveTab] = useState('all'); // Options: 'all', 'pending', 'active', 'completed'

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Add formErrors state
  const [formErrors, setFormErrors] = useState({});

  // Define loadVisitors function
  const loadVisitors = async () => {
    try {
      setLoading(true);
      console.log('Fetching visitor data for owner...');

      // Get visitors data for owner villas
      const [visitorsResponse, villasResponse] = await Promise.all([
        visitorAPI.getOwnerVisitors(),
        api.get('/api/villas/owner')
      ]);

      console.log('Visitors response:', visitorsResponse.data);
      console.log('Villas response:', villasResponse.data);

      // Store owner's villas for the form
      if (Array.isArray(villasResponse.data)) {
        setVillas(villasResponse.data);

        // Set default villa_id for new visitors if owner has villas
        if (villasResponse.data.length > 0) {
          setFormData(prev => ({
            ...prev,
            villa_id: villasResponse.data[0].id
          }));
        }
      } else {
        setVillas([]); // Initialize as empty array if no data
      }

      // Process visitors data
      if (Array.isArray(visitorsResponse.data)) {
        // Ensure each visitor has proper status set
        const processedVisitors = visitorsResponse.data.map(visitor => ({
          ...visitor,
          status: visitor.time_out ? 'completed' :
            visitor.time_in && !visitor.approval_status ? 'pending_approval' :
              visitor.time_in && visitor.approval_status === 'approved' ? 'active' :
                visitor.status || 'pending_approval',
          // Handle both villa_id and unit_id
          villa_id: visitor.villa_id || visitor.unit_id,
          unit_id: visitor.unit_id || visitor.villa_id,
          // Make sure IDs are integers
          id: typeof visitor.id === 'string' ? parseInt(visitor.id) : visitor.id
        }));

        // Filter out any visitors that don't belong to this owner's villas
        const ownerVillaIds = villasResponse.data.map(villa => villa.id);
        const filteredVisitors = processedVisitors.filter(visitor =>
          ownerVillaIds.includes(visitor.villa_id) || ownerVillaIds.includes(visitor.unit_id)
        );

        console.log('Processed visitors:', filteredVisitors);
        setVisitors(filteredVisitors);
      } else {
        setVisitors([]); // Initialize as empty array if no data
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching visitors:', err);
      setError('Failed to load visitors. ' + (err.message || ''));

      // Initialize with empty arrays on error
      setVisitors([]);
      setVillas([]);
    } finally {
      setLoading(false);
    }
  };

  // Effect to prevent body scrolling when modal is open
  useEffect(() => {
    if (showAddForm || showDetailsModal || showEditForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showAddForm, showDetailsModal, showEditForm]);

  // Fetch visitors and villas data
  useEffect(() => {
    if (currentUser && currentUser.role === 'owner') {
      loadVisitors();
    } else {
      // Initialize with empty arrays if not an owner
      setVisitors([]);
      setVillas([]);
      setLoading(false);
    }
  }, [currentUser]);

  // Filter visitors based on status and search term
  const getFilteredVisitors = () => {
    // First filter by active tab
    let tabFiltered = visitors;
    if (activeTab === 'pending') {
      tabFiltered = visitors.filter(v => v.status === 'pending_approval' || (!v.approval_status && v.time_in));
    } else if (activeTab === 'active') {
      tabFiltered = visitors.filter(v => v.status === 'active' || (v.time_in && v.approval_status === 'approved' && !v.time_out));
    } else if (activeTab === 'completed') {
      tabFiltered = visitors.filter(v => v.status === 'completed' || v.time_out);
    }

    // Then apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return tabFiltered.filter(visitor =>
        visitor.visitor_name.toLowerCase().includes(searchLower) ||
        visitor.purpose.toLowerCase().includes(searchLower) ||
        (visitor.villa_number || '').toLowerCase().includes(searchLower)
      );
    }

    return tabFiltered;
  };

  // Get filtered visitors
  const filteredVisitors = getFilteredVisitors();

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredVisitors.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredVisitors.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('visitors-table-section');
    }, 50);
  };

  // Previous page
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToSectionTop('visitors-table-section');
      }, 50);
    }
  };

  // Next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToSectionTop('visitors-table-section');
      }, 50);
    }
  };

  // Update validateForm function
  const validateForm = () => {
    const errors = {};
    const trimmedName = formData.visitor_name.trim();
    const trimmedPurpose = formData.purpose.trim();
    const trimmedPhone = formData.phone.trim();

    if (!trimmedName) {
      errors.visitor_name = 'Visitor name is required';
    }

    if (!formData.villa_id) {
      errors.villa_id = 'Please select a villa';
    }

    if (!trimmedPurpose) {
      errors.purpose = 'Purpose of visit is required';
    }

    if (trimmedPhone && !validatePhoneNumber(trimmedPhone).isValid) {
      errors.phone = validatePhoneNumber(trimmedPhone).message;
    }

    if (!formData.expected_date) {
      errors.expected_date = 'Expected date is required';
    }

    if (!formData.expected_time) {
      errors.expected_time = 'Expected time is required';
    }

    if (!formData.expected_duration) {
      errors.expected_duration = 'Expected duration is required';
    }

    setFormErrors(errors);
    return errors;
  };

  const handleSubmitVisitor = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      // Show first error message
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }

    setFormSubmitting(true);
    try {
      // Format data for API with trimmed values
      const apiData = {
        visitor_name: formData.visitor_name.trim(),
        unit_id: parseInt(formData.villa_id),
        purpose: formData.purpose.trim(),
        phone: formData.phone.trim(),
        expected_date: formData.expected_date,
        expected_time: formData.expected_time,
        expected_duration: formData.expected_duration,
        notes: formData.notes.trim()
      };

      console.log('Submitting visitor data:', apiData);

      // Call API to create new visitor
      const response = await api.post('/api/visitors/register', apiData);
      console.log('Visitor created:', response.data);

      // Close form and refresh list
      setShowAddForm(false);
      loadVisitors();

      // Reset form
      setFormData({
        visitor_name: '',
        villa_id: villas.length > 0 ? villas[0].id : '',
        purpose: '',
        phone: '',
        expected_date: formatDate(new Date(Date.now() + 24 * 60 * 60 * 1000)),
        expected_time: '14:00',
        expected_duration: '1',
        notes: ''
      });

      // Clear errors
      setFormErrors({});
      setPhoneError('');

      toast.success('Visitor added successfully!');
    } catch (err) {
      console.error('Error creating visitor:', err);
      toast.error(err.response?.data?.message || 'Failed to create visitor');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Edit visitor function
  // const handleEditVisitor = (visitor) => {
  //   // Set the selected visitor for the edit form
  //   setSelectedVisitor(visitor);

  //   // Populate form with visitor data
  //   setFormData({
  //     visitor_name: visitor.visitor_name,
  //     villa_id: visitor.villa_id.toString(),
  //     purpose: visitor.purpose,
  //     phone: visitor.phone || '',
  //     expected_date: visitor.expected_date,
  //     expected_time: visitor.expected_time,
  //     expected_duration: visitor.expected_duration,
  //     notes: visitor.notes || ''
  //   });

  //   // Close details modal if open
  //   if (showDetailsModal) {
  //     setShowDetailsModal(false);
  //   }

  //   // Clear validation errors
  //   setPhoneError('');

  //   // Show edit form
  //   setShowEditForm(true);
  // };

  // Submit edited visitor data
  const handleSubmitEditedVisitor = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      return;
    }

    setFormSubmitting(true);
    try {
      // Only allow editing upcoming or denied visitors
      if (selectedVisitor.status !== 'upcoming' && selectedVisitor.status !== 'denied') {
        throw new Error('Only upcoming or denied visitors can be edited');
      }

      // Format data for API with trimmed values
      const apiData = {
        visitor_name: formData.visitor_name.trim(),
        unit_id: parseInt(formData.villa_id),
        purpose: formData.purpose.trim(),
        phone: formData.phone.trim(),
        expected_date: formData.expected_date,
        expected_time: formData.expected_time,
        expected_duration: formData.expected_duration,
        notes: formData.notes.trim()
      };

      console.log('Updating visitor data:', apiData);

      // Call API to update visitor
      const response = await visitorAPI.updateVisitor(selectedVisitor.id, apiData);
      console.log('Visitor updated:', response.data);

      // Update visitor in the list
      const selectedVilla = villas.find(v => v.id === parseInt(formData.villa_id));
      const updatedVisitor = {
        ...selectedVisitor,
        visitor_name: formData.visitor_name.trim(),
        unit_id: parseInt(formData.villa_id),
        villa_number: selectedVilla?.villa_number || '',
        purpose: formData.purpose.trim(),
        phone: formData.phone.trim(),
        expected_date: formData.expected_date,
        expected_time: formData.expected_time,
        expected_duration: formData.expected_duration,
        notes: formData.notes.trim(),
        status: selectedVisitor.status || 'upcoming'
      };

      setVisitors(visitors.map(v => v.id === selectedVisitor.id ? updatedVisitor : v));
      setSelectedVisitor(null);

      // Reset form and close modal
      setFormData({
        visitor_name: '',
        villa_id: villas.length > 0 ? villas[0].id : '',
        purpose: '',
        phone: '',
        expected_date: formatDate(new Date(Date.now() + 24 * 60 * 60 * 1000)),
        expected_time: '14:00',
        expected_duration: '1',
        notes: ''
      });

      // Clear validation errors
      setFormErrors({});
      setPhoneError('');
      setShowEditForm(false);

      toast.success('Visitor updated successfully!');
    } catch (err) {
      console.error('Error updating visitor:', err);
      toast.error(err.response?.data?.message || 'Failed to update visitor');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Delete visitor function
  // const handleDeleteVisitor = async (visitorId) => {
  //   if (!window.confirm('Are you sure you want to cancel this visitor registration?')) {
  //     return;
  //   }

  //   try {
  //     setLoading(true);

  //     // Call API to delete visitor
  //     await visitorAPI.deleteVisitor(visitorId);

  //     // Remove visitor from list
  //     setVisitors(visitors.filter(v => v.id !== visitorId));

  //     // Close details modal if open
  //     if (showDetailsModal) {
  //       setShowDetailsModal(false);
  //       setSelectedVisitor(null);
  //     }

  //     alert('Visitor registration cancelled successfully');
  //   } catch (err) {
  //     console.error('Error cancelling visitor:', err);
  //     alert('Failed to cancel visitor: ' + (err.response?.data?.message || err.message || 'Please try again.'));
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Create local placeholder image using data URL
  const createLocalPlaceholder = (text = 'No Image', width = 200, height = 200) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, height);

    // Border
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);

    // Text
    ctx.fillStyle = '#6c757d';
    ctx.font = '16px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, width / 2, height / 2);

    return canvas.toDataURL();
  };

  // const getImageUrl = (url, fallback = 'No Image') => {
  //   if (!url) return createLocalPlaceholder(fallback);

  //   try {
  //     // If it's already a data URL, return as is
  //     if (url.startsWith('data:image')) {
  //       return url;
  //     }

  //     // If it's a relative URL, make it absolute
  //     if (url.startsWith('/')) {
  //       const apiBaseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');
  //       return `${apiBaseUrl}${url}`;
  //     }

  //     // If it's already absolute, return as is
  //     return url;
  //   } catch (error) {
  //     console.log('Error processing image URL:', error);
  //     return createLocalPlaceholder(fallback);
  //   }
  // };

  const handleViewDetails = async (visitor) => {
    try {
      setLoading(true);

      // Reset direct image sources first
      setDirectVisitorImageSrc('');
      setDirectIdDocImageSrc('');

      // Set initial visitor details
      setSelectedVisitor(visitor);
      setShowDetailsModal(true);

      // Fetch complete visitor details
      try {
        const response = await api.get(`/api/visitors/${visitor.id}`);
        if (response.data) {
          // Process photo URLs
          if (response.data.photo_url) {
            const photoUrl = getVisitorPhotoUrl({ ...visitor, ...response.data });
            setDirectVisitorImageSrc(photoUrl);
          }

          // Process ID document URLs
          if (response.data.id_document_url) {
            const idDocUrl = getIdDocumentUrl({ ...visitor, ...response.data });
            setDirectIdDocImageSrc(idDocUrl);
          }

          // Update with fetched data
          setSelectedVisitor({
            ...visitor,
            ...response.data,
            // Keep original URLs to avoid undefined
            photo_url: response.data.photo_url || visitor.photo_url,
            id_document_url: response.data.id_document_url || visitor.id_document_url
          });
        }
      } catch (error) {
        console.error('Error fetching visitor details:', error);
        // Continue with what we have
      }
    } catch (error) {
      console.error('Error in handleViewDetails:', error);
    } finally {
      setLoading(false);
    }
  };

  // Modify the handleApproveVisitor function to use context
  // const { approveVisitor } = useVisitorData();

  const handleVisitorApproval = async (visitorId, approved) => {
    try {
      setLoading(true);

      const response = await api.post(`/api/visitors/${visitorId}/approve`, {
        approved: approved,
        approval_status: approved ? 'approved' : 'rejected',
        status: approved ? 'active' : 'rejected'
      });

      if (response.data.success) {
        // Update the visitor in the local state
        setVisitors(prev => prev.map(visitor => {
          if (visitor.id === visitorId) {
            return {
              ...visitor,
              status: approved ? 'active' : 'rejected',
              approval_status: approved ? 'approved' : 'rejected'
            };
          }
          return visitor;
        }));

        toast.success(`Visitor ${approved ? 'approved' : 'rejected'} successfully`);

        // Close the details modal if it's open
        if (showDetailsModal) {
          setShowDetailsModal(false);
        }

        // Refresh the visitors list to get the latest data
        await loadVisitors();
      } else {
        toast.error('Failed to update visitor status');
      }
    } catch (error) {
      console.error('Error approving/rejecting visitor:', error);
      toast.error('Failed to update visitor status: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Add status badge function
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_approval':
        return <span className="badge bg-warning">Pending Approval</span>;
      case 'active':
        return <span className="badge bg-success">Active</span>;
      case 'rejected':
        return <span className="badge bg-danger">Rejected</span>;
      case 'completed':
        return <span className="badge bg-secondary">Completed</span>;
      default:
        return <span className="badge bg-info">{status}</span>;
    }
  };

  // Helper functions
  function formatDate(date) {
    const d = new Date(date);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  }

  // function yesterday() {
  //   const date = new Date();
  //   date.setDate(date.getDate() - 1);
  //   return date;
  // }

  function formatDateTime(dateString) {
    if (!dateString) return 'N/A';

    // Parse "YYYY-MM-DD HH:mm:ss" as UTC
    const [datePart, timePart] = dateString.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);

    // Create a UTC Date object
    const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

    // Format to IST using timeZone
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(utcDate);
  }

  // Helper function to format duration display
  const formatDuration = (duration) => {
    if (!duration) return 'Not specified';

    const hours = parseFloat(duration);

    if (hours < 1) {
      return `${hours * 60} minutes`;
    } else if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else if (hours < 168) {
      const days = Math.round(hours / 24);
      return `${days} day${days !== 1 ? 's' : ''}`;
    } else if (hours < 720) {
      const weeks = Math.round(hours / 168);
      return `${weeks} week${weeks !== 1 ? 's' : ''}`;
    } else {
      const months = Math.round(hours / 720);
      return `${months} month${months !== 1 ? 's' : ''}`;
    }
  };

  // Add this helper function to get image URL
  const getVisitorPhotoUrl = (visitor) => {
    if (!visitor) return createLocalPlaceholder('No Photo');

    try {
      // Add timestamp for cache busting
      const timestamp = Date.now();
      const baseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      // Use direct photo endpoint as the first choice - most reliable method
      if (visitor.id) {
        return `${baseUrl}/api/visitors/${visitor.id}/direct-photo?t=${timestamp}&bypass=true`;
      }

      // Fallback to photo_url if available
      if (visitor.photo_url) {
        // Add cache busting
        return sanitizeUrl(visitor.photo_url, timestamp);
      }

      // Last resort - placeholder
      return createLocalPlaceholder('No Photo');
    } catch (error) {
      console.error('Error generating visitor photo URL:', error);
      return createLocalPlaceholder('No Photo');
    }
  };

  const getIdDocumentUrl = (visitor) => {
    if (!visitor) return createLocalPlaceholder('No ID');

    try {
      // Add timestamp for cache busting
      const timestamp = Date.now();
      const baseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      // Use direct ID document endpoint as the first choice - most reliable method
      if (visitor.id) {
        return `${baseUrl}/api/visitors/${visitor.id}/direct-id-document?t=${timestamp}&bypass=true`;
      }

      // Fallback to id_document_url if available
      if (visitor.id_document_url) {
        // Add cache busting
        return sanitizeUrl(visitor.id_document_url, timestamp);
      }

      // Last resort - placeholder
      return createLocalPlaceholder('No ID');
    } catch (error) {
      console.error('Error generating ID document URL:', error);
      return createLocalPlaceholder('No ID');
    }
  };

  // Updated sanitizeUrl to support timestamp parameter
  const sanitizeUrl = (url, timestamp = null) => {
    if (!url) return null;

    try {
      // Check if it's a data URL - return as-is
      if (url.startsWith('data:')) {
        return url;
      }

      const baseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');
      let resultUrl;

      // Check if it's an absolute URL already
      if (url.startsWith('http')) {
        resultUrl = url;
      } else {
        // It's a relative URL - handle with care to avoid duplicates
        // Remove leading slash if present for consistent processing
        let cleanPath = url.startsWith('/') ? url.substring(1) : url;

        // Create complete URL
        resultUrl = `${baseUrl}/${cleanPath}`;
      }

      // Add timestamp for cache busting if requested
      if (timestamp) {
        resultUrl += resultUrl.includes('?') ? `&t=${timestamp}` : `?t=${timestamp}`;
      }

      return resultUrl;
    } catch (error) {
      console.error('Error sanitizing URL:', error);
      return url; // Return original if there's an error
    }
  };

  // Improved image error handler to prevent infinite loops
  const handleImageLoadError = (e, fallbackText = 'No Image') => {
    const img = e.target;

    // Critical safeguard: Only handle the error once to prevent loops
    if (img.hasAttribute('data-error-handled')) {
      // Already handled this error, don't retry again
      return;
    }

    // Mark as handled immediately to prevent retries
    img.setAttribute('data-error-handled', 'true');

    try {
      // Try a direct endpoint as a single fallback
      if (img.dataset && img.dataset.visitorId) {
        const visitorId = img.dataset.visitorId;
        const isIdDoc = img.alt && img.alt.toLowerCase().includes('id');
        const endpoint = isIdDoc ? 'direct-id-document' : 'direct-photo';
        const timestamp = Date.now();
        const baseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

        // Use the direct endpoint with cache busting
        img.src = `${baseUrl}/api/visitors/${visitorId}/${endpoint}?t=${timestamp}&bypass=true`;
      } else {
        // If we can't retry, use local placeholder
        img.src = createLocalPlaceholder(fallbackText);
      }
    } catch (error) {
      // Last resort fallback
      img.src = createLocalPlaceholder('No Image');
    }
  };

  // Update handleInputChange to clear errors when user types
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }

    // Special handling for phone number field
    if (name === 'phone') {
      // Remove all non-numeric characters
      const numericValue = value.replace(/\D/g, '');

      // Limit to 10 digits
      const limitedValue = numericValue.slice(0, 10);

      setFormData(prev => ({
        ...prev,
        [name]: limitedValue
      }));

      // Real-time validation for phone number
      if (limitedValue) {
        const validation = validatePhoneNumber(limitedValue);
        if (!validation.isValid) {
          setFormErrors(prev => ({ ...prev, phone: validation.message }));
        } else {
          setFormErrors(prev => ({ ...prev, phone: null }));
        }
      } else {
        setFormErrors(prev => ({ ...prev, phone: null }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Phone number validation function
  const validatePhoneNumber = (phone) => {
    if (!phone) return { isValid: true, message: '' }; // Phone is optional

    // Check if it's exactly 10 digits
    if (phone.length !== 10) {
      return { isValid: false, message: 'Phone number must be exactly 10 digits' };
    }

    // Check if it starts with valid digits (6-9 for Indian mobile numbers)
    if (!/^[6-9]/.test(phone)) {
      return { isValid: false, message: 'Mobile number must start with 6, 7, 8, or 9' };
    }

    return { isValid: true, message: '' };
  };

  // Add new function to handle modal close
  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setFormErrors({});
    setPhoneError('');
    setFormData({
      visitor_name: '',
      villa_id: villas.length > 0 ? villas[0].id : '',
      purpose: '',
      phone: '',
      expected_date: formatDate(new Date(Date.now() + 24 * 60 * 60 * 1000)),
      expected_time: '14:00',
      expected_duration: '1',
      notes: ''
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <PageTitle
        icon="fa-users"
        title="My Visitors"
        action={
          <div className='d-flex flex-wrap gap-2 align-items-center'>
            <Link to="/owner/dashboard" className="btn btn-outline-primary">
              <i className="fas fa-arrow-left me-2"></i> Back to Dashboard
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => {
                setFormErrors({});
                setPhoneError('');
                setShowAddForm(true);
              }}
              disabled={villas.length === 0}
            >
              <i className="fas fa-plus me-2"></i> New Visitor
            </button>
          </div>
        }
      />
      <p className="text-muted mb-4">Manage visitors to your villas</p>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {villas.length === 0 && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          You need to have a villa assigned to your account before you can register visitors.
        </div>
      )}

      {/* Visitor Request Alerts */}
      {getFilteredVisitors().filter(v => v.status === 'pending_approval').length > 0 && (
        <div className="alert alert-warning mb-4">
          <div className="d-flex justify-content-between align-items-center gap-2">
            <div>
              <i className="fas fa-bell me-2"></i>
              <strong>New Visitor Requests Pending Approval ({getFilteredVisitors().filter(v => v.status === 'pending_approval').length})</strong>
            </div>
            <button
              className="btn btn-warning btn-sm"
              onClick={() => setActiveTab('pending')}
            >
              View Requests
            </button>
          </div>
        </div>
      )}

      {/* Enhanced Tabs with Theme Integration */}
      <div className="mb-4">
        <style>{`
          .enhanced-visitor-tabs {
            display: flex;
            background: #f8f9fa;
            border-radius: 12px;
            padding: 6px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            border: 1px solid #e9ecef;
            overflow-x: auto;
            gap: 4px;
          }
          
          .enhanced-tab-button {
            flex: 1;
            min-width: 120px;
            padding: 12px 20px;
            border: none;
            background: transparent;
            color: #6c757d;
            font-weight: 500;
            font-size: 14px;
            border-radius: 8px;
            transition: all 0.3s ease;
            position: relative;
            white-space: nowrap;
            cursor: pointer;
          }
          
          .enhanced-tab-button:hover:not(.active) {
            background: rgba(${settings.theme_primary_color ?
            `${parseInt(settings.theme_primary_color.slice(1, 3), 16)}, ${parseInt(settings.theme_primary_color.slice(3, 5), 16)}, ${parseInt(settings.theme_primary_color.slice(5, 7), 16)}`
            : '13, 110, 253'}, 0.1);
            color: ${settings.theme_primary_color || '#0d6efd'};
            transform: translateY(-1px);
          }
          
          .enhanced-tab-button.active {
            background: ${settings.theme_primary_color || '#0d6efd'};
            color: white;
            box-shadow: 0 4px 12px rgba(${settings.theme_primary_color ?
            `${parseInt(settings.theme_primary_color.slice(1, 3), 16)}, ${parseInt(settings.theme_primary_color.slice(3, 5), 16)}, ${parseInt(settings.theme_primary_color.slice(5, 7), 16)}`
            : '13, 110, 253'}, 0.3);
            transform: translateY(-2px);
          }
          
          .tab-badge {
            position: absolute;
            top: -6px;
            right: -6px;
            background: #dc3545;
            color: white;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            font-size: 11px;
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          
          @media (max-width: 768px) {
            .enhanced-visitor-tabs {
              padding: 4px;
              gap: 2px;
            }
            
            .enhanced-tab-button {
              min-width: unset;
              padding: 10px 16px;
              font-size: 13px;
            }
            
            .tab-badge {
              width: 18px;
              height: 18px;
              font-size: 10px;
            }
          }
        `}</style>

        <div className="enhanced-visitor-tabs">
          <button
            className={`enhanced-tab-button ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Visitors
          </button>
          <button
            className={`enhanced-tab-button ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending Approval
            {getFilteredVisitors().filter(v => v.status === 'pending_approval').length > 0 && (
              <span className="tab-badge">
                {getFilteredVisitors().filter(v => v.status === 'pending_approval').length}
              </span>
            )}
          </button>
          <button
            className={`enhanced-tab-button ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            Active
          </button>
          <button
            className={`enhanced-tab-button ${activeTab === 'completed' ? 'active' : ''}`}
            onClick={() => setActiveTab('completed')}
          >
            Completed
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-12">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search visitors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Display visitor list based on active tab */}
      <div className="card border-0 shadow-lg rounded overflow-hidden mb-4" id="visitors-table-section">
        <div
          className="card-header"
          style={{
            backgroundColor: settings.block_title_bg_color || '#f8f9fa',
            color: settings.block_title_color || '#8B4513'
          }}
        >
          <h5 className="mb-0">
            <i
              className="fas fa-users-alt me-2"
              style={{ color: settings.block_title_color || '#8B4513' }}
            ></i>
            {activeTab === 'pending' ? 'Pending Approval Requests' :
              activeTab === 'active' ? 'Active Visitors' :
                activeTab === 'completed' ? 'Completed Visits' : 'All Visitors'}
          </h5>
        </div>
        <div className="list-group list-group-flush">
          {filteredVisitors.length === 0 ? (
            <div className="alert alert-info mx-3">
              <i className="fas fa-info-circle me-2"></i>
              No visitor found.
            </div>
          ) : (
            currentItems.map(visitor => (
              <div
                key={visitor.id}
                className={`list-group-item py-3 ${visitor.status === 'pending_approval' ? 'bg-light border-start border-warning border-4' : ''}`}
              >
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                  <div className='mb-2'>
                    <h5 className="mb-1">
                      {visitor.visitor_name}
                      {visitor.status === 'pending_approval' && (
                        <span className="badge bg-warning ms-2">
                          <i className="fas fa-clock me-1"></i>
                          Pending Your Approval
                        </span>
                      )}
                    </h5>
                    <div className="d-flex flex-wrap mb-2">
                      <div className="me-3">
                        <i className="fas fa-home me-1"></i> Villa {visitor.villa_number || visitor.unit_number}
                      </div>
                      <div className="me-3">
                        <i className="fas fa-calendar-alt me-1"></i> {visitor.expected_date || visitor.time_in?.slice(0, 10) || 'Not specified'}
                      </div>
                      <div>
                        <i className="fas fa-clock me-1"></i> Duration: {formatDuration(visitor.expected_duration)}
                      </div>
                    </div>

                    <div className="mb-1">
                      <strong>Purpose:</strong> {visitor.purpose}
                    </div>

                    {visitor.time_in && (
                      <div className="small text-muted">
                        <strong>Check-in:</strong> {formatDateTime(visitor.time_in)}
                        {visitor.time_out && (
                          <span className="ms-3">
                            <strong>Check-out:</strong> {formatDateTime(visitor.time_out)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <div>
                      {getStatusBadge(visitor.status)}
                    </div>

                    <div className="btn-group">
                      {visitor.status === 'pending_approval' && (
                        <>
                          <button
                            className="btn btn-success btn-sm me-2"
                            onClick={() => handleVisitorApproval(visitor.id, true)}
                          >
                            <i className="fas fa-check me-1"></i>
                            Approve
                          </button>
                          <button
                            className="btn btn-danger btn-sm me-2"
                            onClick={() => handleVisitorApproval(visitor.id, false)}
                          >
                            <i className="fas fa-times me-1"></i>
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => handleViewDetails(visitor)}
                      >
                        <i className="fas fa-eye me-1"></i>
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {/* Pagination */}
        {filteredVisitors.length > 0 && (
          <div className="card-footer d-flex justify-content-between align-items-center">
            <div>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredVisitors.length)} of {filteredVisitors.length} visitors
            </div>

            <nav aria-label="Visitor pagination">
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                </li>

                {/* Simplified pagination - show only certain page numbers */}
                {Array.from({ length: totalPages }).map((_, index) => {
                  const pageNumber = index + 1;

                  // Show first page, last page, current page, and pages around current page
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => paginate(pageNumber)}>
                          {pageNumber}
                        </button>
                      </li>
                    );
                  }

                  // Show ellipsis
                  if (
                    pageNumber === 2 && currentPage > 3 ||
                    pageNumber === totalPages - 1 && currentPage < totalPages - 2
                  ) {
                    return (
                      <li key={pageNumber} className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }

                  return null;
                })}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Register Visitor Modal */}
      {showAddForm && (
        <>
          {/* Dark overlay backdrop */}
          <div
            className="modal-backdrop show"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#000',
              opacity: 0.5,
              zIndex: 1040
            }}
            onClick={handleCloseAddForm}
          ></div>

          {/* Modal dialog */}
          <div
            className="modal show"
            style={{
              display: 'block',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1050,
              overflow: 'auto',
              outline: 0
            }}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-lg" role="document" style={{
              margin: '1.75rem auto',
              maxHeight: 'calc(100vh - 3.5rem)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div className="modal-content" style={{ maxHeight: 'calc(100vh - 3.5rem)' }}>
                <div
                  className="modal-header"
                  style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}
                >
                  <h5 className="modal-title">Register a New Visitor</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseAddForm}
                    disabled={formSubmitting}
                    aria-label="Close"
                    style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                  ></button>
                </div>
                <form onSubmit={handleSubmitVisitor} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: 'calc(100vh - 7rem)' }}>
                  <div className="modal-body" style={{ overflowY: 'auto' }}>
                    <div className="mb-3">
                      <label htmlFor="villa_id" className="form-label">Select Villa</label>
                      <select
                        className={`form-select ${formErrors.villa_id ? 'is-invalid' : ''}`}
                        id="villa_id"
                        name="villa_id"
                        value={formData.villa_id}
                        onChange={handleInputChange}
                      >
                        {villas.map(villa => (
                          <option key={villa.id} value={villa.id}>
                            {villa.villa_number}
                          </option>
                        ))}
                      </select>
                      {formErrors.villa_id && <div className="invalid-feedback">{formErrors.villa_id}</div>}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="visitor_name" className="form-label">Visitor Name</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.visitor_name ? 'is-invalid' : ''}`}
                        id="visitor_name"
                        name="visitor_name"
                        value={formData.visitor_name}
                        onChange={handleInputChange}
                        placeholder="Enter visitor's name"
                      />
                      {formErrors.visitor_name && <div className="invalid-feedback">{formErrors.visitor_name}</div>}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="expected_date" className="form-label">Expected Date</label>
                      <input
                        type="date"
                        className={`form-control ${formErrors.expected_date ? 'is-invalid' : ''}`}
                        id="expected_date"
                        name="expected_date"
                        value={formData.expected_date}
                        onChange={handleInputChange}
                        min={formatDate(new Date())}
                      />
                      {formErrors.expected_date && <div className="invalid-feedback">{formErrors.expected_date}</div>}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="expected_time" className="form-label">Expected Time</label>
                      <input
                        type="time"
                        className={`form-control ${formErrors.expected_time ? 'is-invalid' : ''}`}
                        id="expected_time"
                        name="expected_time"
                        value={formData.expected_time}
                        onChange={handleInputChange}
                      />
                      {formErrors.expected_time && <div className="invalid-feedback">{formErrors.expected_time}</div>}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="purpose" className="form-label">Purpose of Visit</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.purpose ? 'is-invalid' : ''}`}
                        id="purpose"
                        name="purpose"
                        value={formData.purpose}
                        onChange={handleInputChange}
                        placeholder="Enter purpose of visit"
                      />
                      {formErrors.purpose && <div className="invalid-feedback">{formErrors.purpose}</div>}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="expected_duration" className="form-label">Expected Duration</label>
                      <select
                        className={`form-select ${formErrors.expected_duration ? 'is-invalid' : ''}`}
                        id="expected_duration"
                        name="expected_duration"
                        value={formData.expected_duration}
                        onChange={handleInputChange}
                      >
                        <option value="0.5">30 minutes</option>
                        <option value="1">1 hour</option>
                        <option value="2">2 hours</option>
                        <option value="3">3 hours</option>
                        <option value="4">4 hours</option>
                        <option value="5">5 hours</option>
                        <option value="6">6 hours</option>
                        <option value="7">7 hours</option>
                        <option value="8">8 hours</option>
                      </select>
                      {formErrors.expected_duration && <div className="invalid-feedback">{formErrors.expected_duration}</div>}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="phone" className="form-label">Phone Number (Optional)</label>
                      <input
                        type="tel"
                        className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Enter phone number"
                      />
                      {formErrors.phone && <div className="invalid-feedback">{formErrors.phone}</div>}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="notes" className="form-label">Additional Notes</label>
                      <textarea
                        id="notes"
                        name="notes"
                        className="form-control"
                        placeholder="Any additional information for security"
                        rows="3"
                        value={formData.notes}
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseAddForm}
                      disabled={formSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={formSubmitting}
                    >
                      {formSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        <>Register Visitor</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Visitor Details Modal */}
      {showDetailsModal && selectedVisitor && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div
                className="modal-header"
                style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}
              >
                <h5 className="modal-title">Visitor Details #{selectedVisitor.id}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-7">
                    <h6>Visitor Information</h6>
                    <p><strong>Name:</strong> {selectedVisitor.visitor_name}</p>
                    <p><strong>Phone:</strong> {selectedVisitor.phone || 'Not provided'}</p>
                    <p><strong>Unit:</strong> Villa {selectedVisitor.villa_number}</p>
                    <p><strong>Purpose:</strong> {selectedVisitor.purpose}</p>
                    <p><strong>Status:</strong> {getStatusBadge(selectedVisitor.status)}</p>
                    <p><strong>Check-in Time:</strong> {selectedVisitor.time_in ? formatDateTime(selectedVisitor.time_in) : 'Not checked in'}</p>
                    {selectedVisitor.time_out && (
                      <p><strong>Check-out Time:</strong> {formatDateTime(selectedVisitor.time_out)}</p>
                    )}
                  </div>
                  <div className="col-md-5 bg-light p-3">
                    <h6 className="text-center mb-2 text-muted">Visitor Verification</h6>

                    {/* Visitor Photo */}
                    <div className="mb-3">
                      <p className="small text-center text-muted mb-1">Visitor Photo</p>
                      <div className="d-flex justify-content-center">
                        <div className="img-container d-flex justify-content-center align-items-center border bg-white rounded" style={{ height: '160px', width: '100%' }}>
                          <img
                            src={selectedVisitor.photo_url || (directVisitorImageSrc && directVisitorImageSrc)}
                            alt="Visitor"
                            className="img-fluid"
                            style={{ maxHeight: '145px', maxWidth: '100%', objectFit: 'contain' }}
                            onError={(e) => handleImageLoadError(e, 'No Photo')}
                            crossOrigin="anonymous"
                            data-visitor-id={selectedVisitor?.id}
                          />
                        </div>
                      </div>
                    </div>

                    {/* ID Document Photo */}
                    <div>
                      <p className="small text-center text-muted mb-1">ID Document</p>
                      <div className="d-flex justify-content-center">
                        <div className="img-container d-flex justify-content-center align-items-center border bg-white rounded" style={{ height: '160px', width: '100%' }}>
                          <img
                            src={selectedVisitor.id_document_url || (directIdDocImageSrc && directIdDocImageSrc)}
                            alt="ID Document"
                            className="img-fluid"
                            style={{ maxHeight: '145px', maxWidth: '100%', objectFit: 'contain' }}
                            onError={(e) => handleImageLoadError(e, 'No ID')}
                            crossOrigin="anonymous"
                            data-visitor-id={selectedVisitor?.id}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
                {selectedVisitor.status === 'pending_approval' && (
                  <div>
                    <button
                      className="btn btn-success me-2"
                      onClick={() => handleVisitorApproval(selectedVisitor.id, true)}
                    >
                      <i className="fas fa-check me-1"></i> Approve
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleVisitorApproval(selectedVisitor.id, false)}
                    >
                      <i className="fas fa-times me-1"></i> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Visitor Modal */}
      {showEditForm && selectedVisitor && (
        <>
          {/* Dark overlay backdrop */}
          <div
            className="modal-backdrop show"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#000',
              opacity: 0.5,
              zIndex: 1040
            }}
            onClick={() => !formSubmitting && setShowEditForm(false)}
          ></div>

          {/* Modal dialog */}
          <div
            className="modal show"
            style={{
              display: 'block',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1050,
              overflow: 'auto',
              outline: 0
            }}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-lg" role="document" style={{
              margin: '1.75rem auto',
              maxHeight: 'calc(100vh - 3.5rem)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div className="modal-content" style={{ maxHeight: 'calc(100vh - 3.5rem)' }}>
                <div
                  className="modal-header"
                  style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}
                >
                  <h5 className="modal-title">Edit Visitor</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowEditForm(false)}
                    disabled={formSubmitting}
                    aria-label="Close"
                    style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                  ></button>
                </div>
                <form onSubmit={handleSubmitEditedVisitor} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: 'calc(100vh - 7rem)' }}>
                  <div className="modal-body" style={{ overflowY: 'auto' }}>
                    <div className="mb-3">
                      <label htmlFor="villa_id" className="form-label">Villa</label>
                      <select
                        id="villa_id"
                        name="villa_id"
                        className="form-select"
                        value={formData.villa_id}
                        onChange={handleInputChange}
                        required
                      >
                        {villas.map(villa => (
                          <option key={villa.id} value={villa.id}>
                            Villa {villa.villa_number}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="visitor_name" className="form-label">Visitor Name</label>
                      <input
                        type="text"
                        id="visitor_name"
                        name="visitor_name"
                        className="form-control"
                        placeholder="Enter visitor's full name"
                        value={formData.visitor_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label htmlFor="expected_date" className="form-label">Expected Date</label>
                        <input
                          type="date"
                          id="expected_date"
                          name="expected_date"
                          className="form-control"
                          value={formData.expected_date}
                          onChange={handleInputChange}
                          min={formatDate(new Date())}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="expected_time" className="form-label">Expected Time</label>
                        <input
                          type="time"
                          id="expected_time"
                          name="expected_time"
                          className="form-control"
                          value={formData.expected_time}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label htmlFor="purpose" className="form-label">Purpose of Visit</label>
                        <input
                          type="text"
                          id="purpose"
                          name="purpose"
                          className="form-control"
                          placeholder="e.g., Delivery, Meeting, Maintenance"
                          value={formData.purpose}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="expected_duration" className="form-label">Expected Duration (hours)</label>
                        <select
                          id="expected_duration"
                          name="expected_duration"
                          className="form-select"
                          value={formData.expected_duration}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="0.5">30 minutes</option>
                          <option value="1">1 hour</option>
                          <option value="2">2 hours</option>
                          <option value="3">3 hours</option>
                          <option value="4">4 hours</option>
                          <option value="8">8 hours</option>
                          <option value="24">1 day (24 hours)</option>
                          <option value="48">2 days (48 hours)</option>
                          <option value="72">3 days (72 hours)</option>
                          <option value="168">1 week (7 days)</option>
                          <option value="720">1 month (30 days)</option>
                        </select>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="phone" className="form-label">
                        Phone Number
                        <small className="text-muted">(Optional - 10 digit mobile number)</small>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className={`form-control ${phoneError ? 'is-invalid' : formData.phone && !phoneError ? 'is-valid' : ''}`}
                        placeholder="Enter 10-digit mobile number"
                        value={formData.phone}
                        onChange={handleInputChange}
                        maxLength="10"
                        pattern="[0-9]*"
                        inputMode="numeric"
                      />
                      {phoneError && (
                        <div className="invalid-feedback">
                          {phoneError}
                        </div>
                      )}
                      {formData.phone && !phoneError && formData.phone.length === 10 && (
                        <div className="valid-feedback">
                          Valid mobile number
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="notes" className="form-label">Additional Notes</label>
                      <textarea
                        id="notes"
                        name="notes"
                        className="form-control"
                        placeholder="Any additional information for security"
                        rows="3"
                        value={formData.notes}
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowEditForm(false)}
                      disabled={formSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={formSubmitting}
                    >
                      {formSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>Save Changes</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Visitors; 